import React from 'react';
import { ShareNetwork, Storefront } from '@phosphor-icons/react';

import { MarketShell } from '../../design-system/components/shells';
import {
  Avatar,
  Button,
  Empty,
  FilterRail,
  Pagination,
  PageHeader,
  Pill,
  Rating,
  Skeleton,
} from '../../design-system/components/desktop';
import {
  Cta,
  CtaRow,
  ProfileHead,
  SearchField,
  Sheet,
  SheetBody,
  SheetFooter,
  SheetHeader,
  Avatar as MobileAvatar,
  VerifiedBadge,
} from '../../design-system/components/mobile';
import { useViewport } from '../../design-system/hooks/useViewport';
import { routes } from '../../routes/routes';

import MarketplaceHeader from '../Marketplace/components/MarketplaceHeader/MarketplaceHeader';
import SellerReviews from './SellerReviews';
import MarketplaceFilters from '../Marketplace/components/MarketplaceFilters/MarketplaceFilters';
import { countAppliedFilters } from '../Marketplace/components/MarketplaceFilters/filterModel';
import MarketplaceGrid from '../Marketplace/components/MarketplaceGrid/MarketplaceGrid';
import MarketplaceSorting from '../Marketplace/components/MarketplaceSorting/MarketplaceSorting';

/**
 * Seller store, the view. Pure.
 *
 * The container owns the two queries (the store profile and the store's cards),
 * the URL parameters, the share sheet and the page state.
 *
 * IT IS THE SAME BROWSE SHAPE AS THE MARKETPLACE, ON PURPOSE
 * ----------------------------------------------------------
 * Store and marketplace are one archetype (A1) and they now share the header,
 * the facet panel, the grid, the card and the pagination. The only thing a
 * store adds is the band at the top that says whose shelf this is.
 *
 * FOUR THINGS THE UPSTREAM `store.html` HAS THAT ARE NOT HERE
 * ----------------------------------------------------------
 * 1. PUBLIC REVIEWS. The template shows a row of review cards and a "Reviews"
 *    tab with a count. There is no public reviews endpoint: the audit found the
 *    review data is seller-authenticated only, so a buyer looking at a store
 *    cannot be served it. Inventing the section would mean inventing the data.
 *    Flagged in CONVERSION-MAP.md, Open product questions item 2. The rating
 *    itself IS real and is rendered, because it comes back on the store profile.
 * 2. "Follow store". There is no follow feature in this app.
 * 3. A city under the store name. Shipping is nationwide and we may not state
 *    an origin. `location` is rendered only when the profile actually carries
 *    one and it is the seller's own stated location, never a shipping claim.
 * 4. The Listings / Reviews / About tabs. With reviews unavailable and no about
 *    payload, a tab strip of one tab is chrome pretending to be navigation.
 *
 * `available` MEANS SOMETHING ELSE HERE. On this route the API's `available` is
 * `Sum(quantity)` across the seller's listings for that card, where on
 * `/marketplace` the identical field is `Count(listings)`. The tiles look the
 * same, so the two pages say different words: "N listings" there, "N in stock"
 * here. This is a real API ambiguity, not a design decision, and it is
 * CONVERSION-MAP.md Open product questions item 5.
 */

/** The store band. Desktop identity block; below the handoff, `ProfileHead`. */
function StoreBand({
  isDesktop,
  loading,
  notFound,
  storeName,
  logo,
  verified,
  rating,
  reviewCount,
  onOpenReviews,
  salesCount,
  joined,
  location,
  onShare,
}) {
  if (notFound) {
    return (
      <Empty
        icon={Storefront}
        illustration="not-found"
        /* A 404, not a wall. `locked` is the tone for "you may not see this",
           and nothing here is forbidden: the store simply is not there. One
           scenario, one tone, and `not-found` is neutral everywhere now. */
        title="We could not find that store"
        text="The seller may have closed their store, or the link may be wrong."
        actions={
          isDesktop ? (
            <Button as="a" href={routes.MARKETPLACE} variant="ghost">
              Back to marketplace
            </Button>
          ) : null
        }
      />
    );
  }

  if (loading) {
    /* The skeleton mirrors the band's shape: avatar, name, one meta line. */
    return isDesktop ? (
      <div className="dsk-skeleton-row" aria-hidden="true">
        <Skeleton shape="avatar" />
        <div className="dsk-skeleton-row__main">
          <Skeleton shape="title" width="220px" />
          <Skeleton shape="text" width="160px" />
        </div>
      </div>
    ) : (
      <div aria-hidden="true" style={{ display: 'grid', gap: 8, justifyItems: 'center' }}>
        <Skeleton shape="avatar" />
        <Skeleton shape="title" width="180px" />
        <Skeleton shape="text" width="120px" />
      </div>
    );
  }

  /* THE WAY IN TO THE REVIEWS, and the only one on this page.
     The stars were a `role="img"` with nothing behind them: a buyer could see a
     score and had no way to find out what it rested on. They are a real control
     now WHEN there is something to open -- with no reviews on file the panel
     would be an empty box, so the stars stay inert rather than inviting a tap
     that goes nowhere.

     The `aria-label` REPLACES the button's contents for assistive tech, so the
     name is one clean sentence rather than the star image's label concatenated
     with a count. */
  const ratingNode =
    rating == null ? null : (
      <Rating value={rating} count={reviewCount ?? undefined} size="sm" />
    );

  const ratingControl =
    ratingNode && onOpenReviews && reviewCount > 0 ? (
      <button
        type="button"
        className="dsa-storeband__reviews"
        onClick={onOpenReviews}
        aria-label={`View ${Number(reviewCount).toLocaleString('en-ZA')} reviews`}
      >
        {ratingNode}
      </button>
    ) : (
      ratingNode
    );

  const meta = [
    joined,
    location,
    /* Grouped, because this is the ONE place the number now appears and a bare
       "1240 sales" next to a grouped price column reads as a typo. */
    salesCount != null ? `${Number(salesCount).toLocaleString('en-ZA')} sales` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  /* Both tracks' avatars are initials blocks, which is what `store.html`
     draws. The app also stores an uploaded logo, so it goes in as a child and
     app-layer.css makes it fill the circle. Initials are the fallback. */
  const initials = String(storeName || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase();

  if (!isDesktop) {
    return (
      <ProfileHead
        avatar={
          <MobileAvatar aria-label={storeName}>
            {logo ? <img src={logo} alt="" /> : initials}
          </MobileAvatar>
        }
        name={storeName}
        since={meta || undefined}
      >
        {/* `.ds-profilehead` gives its children no gap at all, so these three
            used to stack flush against each other and against the meta line.
            The wrapper owns the rhythm; see app-layer.css. */}
        <div className="dsa-storeband__actions">
          {(verified || ratingControl) && (
            <div className="dsa-storeband__proof">
              {verified && <VerifiedBadge label="Verified seller" />}
              {/* No `sales` here. The sales count is ALREADY in the meta line
                  above ("March 2024 · 1 240 sales"), and passing it again put
                  the same number on screen twice, once unformatted and once
                  grouped: "1240 sales" and "1 240 sales", two lines apart. */}
              {ratingControl}
            </div>
          )}
          {onShare && (
            <Cta variant="ghost" size="sm" onClick={onShare}>
              Share store
            </Cta>
          )}
        </div>
      </ProfileHead>
    );
  }

  return (
    <PageHeader
      title={storeName}
      actions={
        onShare ? (
          <Button variant="ghost" onClick={onShare}>
            <ShareNetwork /> Share store
          </Button>
        ) : null
      }
    >
      <div
        /* Layout only: the identity row is an avatar beside the corroboration. */
        style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}
      >
        <Avatar label={storeName}>{logo ? <img src={logo} alt="" /> : initials}</Avatar>
        {verified && <Pill tone="success">Verified seller</Pill>}
        {ratingControl}
      </div>
      {meta && <p className="dsk-page-header__sub">{meta}</p>}
    </PageHeader>
  );
}

export function SellerStoreView({
  storeName = 'Seller store',
  logo,
  verified = false,
  rating,
  reviewCount,
  salesCount,
  joined,
  location,
  storeLoading = false,
  storeNotFound = false,
  onShare,

  /* The reviews panel: whether it is open, how to open and close it, and what
     it should show. Fetched by the container on open, never on page load. */
  reviewsOpen = false,
  onOpenReviews,
  onCloseReviews,
  reviews = [],
  reviewsLoading = false,
  reviewsError,
  onRetryReviews,

  cards = [],
  loading = false,
  error,
  onRetry,
  totalCards = 0,
  page = 1,
  totalPages = 1,
  pageSize = 10,
  onPageChange,

  filters,
  onFilterChange,
  /* The page's full reset. See `Marketplace.view` for why this is not
     `onFilterChange(CLEARED_FILTERS)`: the search box is half of what makes
     this page "filtered", so clearing only the facets left a dead button on a
     search-emptied shelf. */
  onClearAll,

  sortBy,
  onSortChange,
  sortOptions = [],

  searchQuery,
  onSearchChange,

  viewMode = 'grid',
  onViewModeChange,

  onCardClick,

  quickAddFor,
  onQuickAdd,
  quickAddedIds,
  /* Which tiles are mid-add. Straight through to the grid, which turns it into
     a real `disabled` on the button -- see the container for where it comes
     from and the grid's own prop comment for why it is keyed on listing id. */
  quickAddPendingIds,

  savedIds,
  onToggleSaved,

  filtersOpen = false,
  onOpenFilters,
  onCloseFilters,
  sortOpen = false,
  onOpenSort,
  onCloseSort,

  shareSurface,

  /* An explicit way back, for when the crumb's parent is not where the buyer
     came from — the same pair `catalog.view.jsx` takes. A store reached from a
     card's seller table sent the buyer to the marketplace on the way out,
     losing the card they were comparing sellers for. */
  backTo,
  backToLabel,
}) {
  const { isDesktop } = useViewport();
  const appliedCount = countAppliedFilters(filters);
  const isFiltered = appliedCount > 0 || Boolean(searchQuery);

  /* Says what the click actually clears -- the reset takes the store search
     with it. */
  const clearLabel = searchQuery ? 'Clear search and filters' : 'Clear all filters';

  const band = (
    <StoreBand
      isDesktop={isDesktop}
      loading={storeLoading}
      notFound={storeNotFound}
      storeName={storeName}
      logo={logo}
      verified={verified}
      rating={rating}
      reviewCount={reviewCount}
      onOpenReviews={onOpenReviews}
      salesCount={salesCount}
      joined={joined}
      location={location}
      onShare={onShare}
    />
  );

  /* The store's own search is scoped to this shelf, so it stays on the page
     rather than moving into the nav, whose search is the whole marketplace.
     That is also why the nav search is switched off on this route: two search
     fields on one screen that mean different things is a trap.

     `.dsk-search` and `.ds-search` are the two tracks' own search pills, so
     this picks one rather than putting a `ds-` molecule inside a `dsk-` rail. */
  const storeSearch = isDesktop ? (
    <label className="dsk-search">
      <input
        type="search"
        value={searchQuery || ''}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search this store"
        autoComplete="off"
      />
      <span className="dsk-sr-only">{`Search ${storeName}`}</span>
    </label>
  ) : (
    <SearchField
      value={searchQuery || ''}
      onChange={(event) => onSearchChange(event.target.value)}
      placeholder="Search this store by card name, number or ID"
      aria-label={`Search ${storeName}`}
    />
  );

  const header = (
    <MarketplaceHeader
      /* NOT `storeName`. The identity band above is this page's title and it
         already carries the store name as an h1; passing it again rendered the
         same name twice as two `dsk-page-title` h1s, one directly under the
         other. In the pre-conversion page the two headings said DIFFERENT
         things: the band was the STORE name and this one was the seller's own
         name (`sellerInfo?.name`, falling back to the literal "Seller Store"),
         so the duplication is new. Naming the section instead keeps the
         heading, keeps the count, and matches `/marketplace`, where the same
         component renders "Marketplace" over "2 847 cards". */
      title="Listings"
      sortBy={sortBy}
      onSortChange={onSortChange}
      sortOptions={sortOptions}
      viewMode={viewMode}
      onViewModeChange={onViewModeChange}
      onOpenFilters={onOpenFilters}
      onOpenSort={onOpenSort}
      appliedCount={appliedCount}
      totalCards={loading ? null : totalCards}
      countLabel="listings"
    />
  );

  const results = (
    <>
      <MarketplaceGrid
        cards={cards}
        viewMode={viewMode}
        loading={loading}
        error={error}
        onRetry={onRetry}
        onCardClick={onCardClick}
        cardHref={(card) => `${routes.MARKETPLACE}/card/${card.id}`}
        savedIds={savedIds}
        onToggleSaved={onToggleSaved}
        /* Add to cart on the tile itself. Every tile here belongs to ONE
           seller, which is what makes it answerable at all -- see the grid's
           own note on why `/marketplace` does not get this. */
        quickAddFor={quickAddFor}
        onQuickAdd={onQuickAdd}
        quickAddedIds={quickAddedIds}
        quickAddPendingIds={quickAddPendingIds}
        /* Sum(quantity) on this route, NOT a listing count. Different word for
           the different meaning, because the tiles are otherwise identical.
           At zero the tile says nothing at all, same as `/marketplace`. */
        availabilityLabel={(available) => (available > 0 ? `${available} in stock` : undefined)}
        emptyTone={isFiltered ? 'filtered' : 'default'}
        emptyTitle={
          isFiltered ? 'Nothing in this store matches' : 'This store has nothing listed'
        }
        emptyText={
          isFiltered
            ? searchQuery
              ? 'No cards in this store match '
              : 'This seller stocks nothing that fits those filters. Widen one and their cards come back.'
            : 'When this seller lists a card it shows up here.'
        }
        emptyQuery={isFiltered && searchQuery ? searchQuery : undefined}
        emptyActions={
          isFiltered ? (
            isDesktop ? (
              <Button variant="ghost" onClick={onClearAll}>
                {clearLabel}
              </Button>
            ) : (
              <Cta variant="ghost" size="sm" onClick={onClearAll}>
                {clearLabel}
              </Cta>
            )
          ) : null
        }
      />

      {!loading && !error && totalPages > 1 && (
        <Pagination
          /* This page is the marketplace's twin: same `.dsa-browse` grid, same
             `MarketplaceHeader` toolbar. It takes the same page-band geometry
             so the two do not drift apart. See app-layer.css. */
          className="dsa-browse-pager"
          page={page}
          pageCount={totalPages}
          onPageChange={onPageChange}
          totalItems={totalCards}
          pageSize={pageSize}
          itemNoun="listings"
        />
      )}
    </>
  );

  return (
    <MarketShell
      activeNav="marketplace"
      title={storeName}
      breadcrumbs={[
        { label: 'Marketplace', to: routes.MARKETPLACE },
        { label: storeName },
      ]}
      /* The CRUMB stays Marketplace whatever route you arrived by: a store
         lives under the marketplace however you got to it. `back` is the
         separate question of where YOU were, and only the caller knows. */
      back={backTo ? { to: backTo, label: `Back to ${backToLabel || 'where you were'}` } : undefined}
      showSearch={false}
    >
      {band}

      {storeNotFound ? null : isDesktop ? (
        <>
          {header}
          <div className="dsa-browse">
            <FilterRail label="Filter this store's listings">
              {storeSearch}
              <MarketplaceFilters filters={filters} onFilterChange={onFilterChange} />
            </FilterRail>
            <div>{results}</div>
          </div>
        </>
      ) : (
        <>
          {storeSearch}
          {header}
          {results}

          <Sheet open={filtersOpen} onClose={onCloseFilters}>
            <SheetHeader title="Filters" appliedCount={appliedCount} />
            <SheetBody>
              <MarketplaceFilters
                filters={filters}
                onFilterChange={onFilterChange}
                onClearAll={onClearAll}
                showClearAll={false}
              />
            </SheetBody>
            <SheetFooter>
              <CtaRow>
                {/* Clears and CLOSES, like the marketplace sheet: the store's
                    search box is on the page behind this sheet, so leaving it
                    open after a reset hides the very thing that changed. */}
                <Cta
                  variant="ghost"
                  onClick={() => {
                    if (onClearAll) onClearAll();
                    if (onCloseFilters) onCloseFilters();
                  }}
                >
                  Clear all
                </Cta>
                <Cta variant="solid" onClick={onCloseFilters}>
                  {loading ? 'Show results' : `Show ${totalCards} results`}
                </Cta>
              </CtaRow>
            </SheetFooter>
          </Sheet>

          <Sheet open={sortOpen} onClose={onCloseSort}>
            <SheetHeader title="Sort by" />
            <SheetBody>
              <MarketplaceSorting
                sortOptions={sortOptions}
                value={sortBy}
                onSortChange={onSortChange}
                onClose={onCloseSort}
              />
            </SheetBody>
          </Sheet>
        </>
      )}

      {shareSurface}

      <SellerReviews
        open={reviewsOpen}
        onClose={onCloseReviews}
        storeName={storeName}
        reviews={reviews}
        total={reviewCount}
        loading={reviewsLoading}
        error={reviewsError}
        onRetry={onRetryReviews}
      />
    </MarketShell>
  );
}

export default SellerStoreView;
